import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from 'antd';
import * as API from '../services/api';

const SERVICE_LABELS = {
  milvus: 'Vector Database',
  marker: 'Document Converter',
  graphrag: 'GraphRAG',
};

const SERVICE_HINTS = {
  milvus: 'Search and chat responses may be limited until vector services recover.',
  marker: 'Document parsing is temporarily unavailable.',
  graphrag: 'Knowledge graph actions are temporarily unavailable.',
};

const ServiceAvailabilityBanner = ({ token }) => {
  const [services, setServices] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const load = async () => {
      try {
        const res = await API.dependencyHealthCheck(token, { signal: controller.signal });
        if (!alive) return;
        setServices((res.data || {}).services || null);
      } catch {
        if (!alive) return;
        setServices(null);
      }
    };
    load();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [token]);

  const unavailable = useMemo(() => {
    if (!services || typeof services !== 'object') return [];
    return Object.entries(services)
      .filter(([, data]) => data && data.available === false)
      .map(([key, data]) => ({
        key,
        label: SERVICE_LABELS[key] || key,
        reason: data.reason || 'unavailable',
      }));
  }, [services]);

  useEffect(() => {
    if (!unavailable.length) {
      setDismissed(false);
    }
  }, [unavailable.length]);

  if (!unavailable.length || dismissed) return null;

  const impacted = unavailable.map((item) => item.label).join(', ');
  const details = unavailable
    .map((item) => SERVICE_HINTS[item.key] || `${item.label} is unavailable.`)
    .join(' ');

  return (
    <Alert
      type="warning"
      showIcon
      closable
      onClose={() => setDismissed(true)}
      style={{
        position: 'fixed',
        right: 16,
        bottom: 84,
        width: 420,
        maxWidth: 'calc(100vw - 24px)',
        height: 'auto',
        minHeight: 0,
        zIndex: 1200,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
      }}
      message="Some services are temporarily unavailable"
      description={`Impacted: ${impacted}. ${details}`}
    />
  );
};

export default ServiceAvailabilityBanner;
